import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Theme } from '@twilio-paste/theme';
import { Box } from '@twilio-paste/core';
import ConversationArea from './components/ConversationArea';
import { sendAnalyticsEvent } from './utils/analytics';

function App() {
  // Core state
  const [messages, setMessages] = useState([]);
  const [lastMessageId, setLastMessageId] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  const [language, setLanguage] = useState('en-US');

  // Settings state
  const [filterPhoneNumber, setFilterPhoneNumber] = useState('');

  // Refs
  const pollingRef = useRef(null);
  const callTimerRef = useRef(null);
  const deviceIdentityRef = useRef(null);

  // Load settings from localStorage
  useEffect(() => {
    // Check for targetNumber query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const targetNumber = urlParams.get('targetNumber');
    const language = urlParams.get('language');

    if (language) {
      setLanguage(language);
      console.log('language', language);
    }

    // Only load from localStorage if there's no targetNumber in URL
    // This prevents double updates - we'll let the device registration set it
    if (!targetNumber) {
      const stored = localStorage.getItem('filterPhoneNumber');
      if (stored) {
        setFilterPhoneNumber(stored);
      }
    } else {
      // If URL has targetNumber, use it but don't update localStorage yet
      // The device registration will handle that
      const cleaned = decodeURIComponent(targetNumber).replace(/\s/g, '');
      if (cleaned) {
        setFilterPhoneNumber(cleaned);
      }
    }
  }, []);

  // Callback to update URL and filter when device identity is available
  const handleDeviceIdentity = useCallback((identity) => {
    const targetNumber = `client:${identity}`;

    // Prevent duplicate calls - only update once per identity
    if (deviceIdentityRef.current === identity) {
      return;
    }

    // Check if URL already has this value to prevent unnecessary updates
    const urlParams = new URLSearchParams(window.location.search);
    const currentTargetNumber = urlParams.get('targetNumber');
    if (currentTargetNumber === targetNumber) {
      deviceIdentityRef.current = identity;
      return;
    }

    // Update URL parameter
    const url = new URL(window.location);
    url.searchParams.set('targetNumber', targetNumber);
    window.history.replaceState({}, '', url);

    // Update localStorage and state
    localStorage.setItem('filterPhoneNumber', targetNumber);
    setFilterPhoneNumber(targetNumber);
    deviceIdentityRef.current = identity;

    sendAnalyticsEvent('onLoad');
  }, []);

  // Callback to handle call start
  const handleCallStart = useCallback(() => {
    setCallStartTime(Date.now());
    // Clear previous messages when a new call starts
    setMessages([]);
    setLastMessageId(0);
  }, []);

  // Callback to handle call end
  const handleCallEnd = useCallback(() => {
    setCallStartTime(null);
    // Don't clear messages immediately - let them persist
    // Messages will be cleared when a new call starts or device resets
  }, []);

  // Fetch new messages
  const fetchNewMessages = useCallback(async () => {
    try {
      const effectiveFilter = filterPhoneNumber || '+';
      const url = `/api/messages?lastId=${lastMessageId}&phoneNumber=${decodeURIComponent(
        effectiveFilter
      )}`;

      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (!filterPhoneNumber) {
        if (messages.length > 0) {
          setMessages([]);
          setLastMessageId(0);
        }
        return;
      }

      if (!data.messages || data.messages.length === 0) return;

      if (data.messages && data.messages.length > 0) {
        // Add all messages at once to prevent race conditions
        setMessages((prev) => [...prev, ...data.messages]);

        // Update lastMessageId to the highest ID from all messages
        const maxId = Math.max(
          ...data.messages.map((m) => m.id || 0),
          lastMessageId
        );
        setLastMessageId(maxId);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [filterPhoneNumber, lastMessageId, messages.length]);

  // Start polling when we have a filter phone number (device registered)
  useEffect(() => {
    if (filterPhoneNumber && typeof callStartTime === 'number') {
      pollingRef.current = setInterval(fetchNewMessages, 1000);
      // Don't do initial fetch - let the interval handle it
    } else {
      // Clear polling when no filter phone number
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [filterPhoneNumber, fetchNewMessages, callStartTime]);

  // Start call timer
  useEffect(() => {
    if (callStartTime) {
      callTimerRef.current = setInterval(() => {
        // Timer will be handled by the Sidebar component
      }, 1000);

      return () => {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
      };
    }
  }, [callStartTime]);

  return (
    <Theme.Provider theme="default">
      <Box
        style={{ backgroundColor: '#191F36' }}
        flex="1"
        display="flex"
        height="100vh"
        overflow="hidden"
      >
        <ConversationArea
          messages={messages}
          filterPhoneNumber={filterPhoneNumber}
          onDeviceIdentity={handleDeviceIdentity}
          onCallStart={handleCallStart}
          onCallEnd={handleCallEnd}
          language={language}
        />
      </Box>
    </Theme.Provider>
  );
}

export default App;
